/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import * as React from "react";
import styled, { css } from "styled-components";
import { observer } from "mobx-react";
import { Link, useLocation, useNavigate } from "react-router";
import { ChevronRight, RefreshCw, Search } from "lucide-react";
// Menu was the top bar's sidebar toggle; the sidebar folds from its own foot now (Tech9IQ shell).
// import { ChevronRight, Menu, RefreshCw, Search } from "lucide-react";

import { ThemePalette } from "@src/components/Theme";
import notificationStore from "@src/stores/NotificationStore";
import NotificationDropdown from "@src/components/ui/Dropdowns/NotificationDropdown";
import UserDropdown from "@src/components/ui/Dropdowns/UserDropdown";
import AboutModal from "@src/components/smart/AboutModal";
import userStore from "@src/stores/UserStore";
import shellStore from "@src/components/modules/NavigationModule/Navigation/shellStore";
import {
  itemFor,
  pathSection,
  sectionFor,
  sectionPath,
} from "@src/components/modules/NavigationModule/Navigation/sections";
import { requestRefresh } from "./refresh";

/*
 * The 56 px top bar of every SwiftIQ page, in the Tech9IQ shell: the search box (Ctrl+K / Cmd+K) on
 * the left; on the right the breadcrumbs (earlier levels folded into "..." past two), notifications,
 * the connection dot, the icon-only Refresh and the signed-in user's avatar initial with its menu.
 * (Before: a sidebar menu button on the left and the breadcrumbs in the middle.)
 */
const Bar = styled.header`
  height: 56px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 24px;
  background: ${ThemePalette.surface};
  border-bottom: 1px solid ${ThemePalette.grayscale[0]};
  box-sizing: border-box;
  @media (max-width: 720px) {
    padding: 0 12px;
    gap: 8px;
  }
`;
const iconButton = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: ${ThemePalette.grayscale[5]};
  cursor: pointer;
  &:hover {
    background: ${ThemePalette.grayscale[1]};
    color: ${ThemePalette.black};
  }
  &:focus-visible {
    outline: 2px solid ${ThemePalette.accent};
    outline-offset: 2px;
  }
`;
// const MenuButton = styled.button`
//   ${iconButton}
// `;
const CrumbNav = styled.nav`
  display: flex;
  justify-content: flex-end;
  flex: 1;
  min-width: 0;
`;
const MoreWrap = styled.span`
  position: relative;
  display: inline-flex;
`;
const MoreButton = styled.button`
  ${iconButton}
  width: 24px;
  height: 22px;
  font: inherit;
  font-size: 12px;
  letter-spacing: 0.05em;
`;
const MoreMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 180px;
  z-index: 500;
  padding: 4px;
  border: 1px solid ${ThemePalette.grayscale[0]};
  border-radius: 8px;
  background: var(--t9-bg-raised);
  box-shadow: var(--t9-shadow-xl);
  a,
  span {
    display: block;
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 13px;
    line-height: 18px;
    color: ${ThemePalette.grayscale[4]};
    text-decoration: none;
    white-space: nowrap;
  }
  a:hover,
  a:focus-visible {
    outline: none;
    background: var(--t9-bg-hover);
    color: ${ThemePalette.black};
  }
`;
const CrumbList = styled.ol`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 12px;
  line-height: 16px;
  color: ${ThemePalette.grayscale[5]};
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  @media (max-width: 720px) {
    display: none;
  }
`;
const CrumbItem = styled.li<{ current?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  svg {
    flex-shrink: 0;
    color: ${ThemePalette.grayscale[2]};
  }
  a {
    color: inherit;
    text-decoration: none;
    overflow: hidden;
    text-overflow: ellipsis;
    border-radius: 4px;
  }
  a:hover {
    color: ${ThemePalette.black};
    text-decoration: underline;
  }
  a:focus-visible {
    outline: 2px solid ${ThemePalette.accent};
    outline-offset: 2px;
  }
  ${props =>
    props.current
      ? css`
          font-weight: 500;
          color: ${ThemePalette.grayscale[6]};
        `
      : ""}
`;
const CrumbText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  @media (max-width: 720px) {
    gap: 4px;
  }
`;
const SearchBox = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 320px;
  height: 34px;
  padding: 0 10px;
  margin: 0;
  box-sizing: border-box;
  background: ${ThemePalette.surface};
  border: 1px solid ${ThemePalette.grayscale[2]};
  border-radius: 6px;
  color: ${ThemePalette.grayscale[3]};
  cursor: text;
  &:focus-within {
    border-color: ${ThemePalette.accent};
    box-shadow: 0 0 0 2px ${ThemePalette.accent};
  }
  @media (max-width: 1100px) {
    width: 220px;
  }
  @media (max-width: 720px) {
    width: auto;
    flex: 1;
    min-width: 0;
  }
`;
const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: 0;
  padding: 0;
  background: none;
  color: ${ThemePalette.black};
  font: inherit;
  font-size: 14px;
  outline: none;
  &::placeholder {
    color: ${ThemePalette.grayscale[3]};
  }
`;
const Kbd = styled.kbd`
  font-family: inherit;
  font-size: 11px;
  line-height: 16px;
  padding: 0 5px;
  background: ${ThemePalette.grayscale[7]};
  border: 1px solid ${ThemePalette.grayscale[0]};
  border-radius: 4px;
  color: ${ThemePalette.grayscale[5]};
  white-space: nowrap;
  @media (max-width: 720px) {
    display: none;
  }
`;
const Status = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 30px;
  border-radius: 6px;
  cursor: default;
  &::before {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${ThemePalette.success};
  }
  &:focus-visible {
    outline: 2px solid ${ThemePalette.accent};
    outline-offset: 2px;
  }
`;
const StatusTip = styled.span`
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 8px;
  border-radius: 6px;
  background: var(--t9-tooltip-bg);
  color: var(--t9-tooltip-text);
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 150ms;
  z-index: 200;
  ${Status}:hover &,
  ${Status}:focus-visible & {
    opacity: 1;
  }
`;
// Icon only (Tech9IQ shell): was a bordered "Refresh" button with the text beside the icon.
const RefreshButton = styled.button<{ busy?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 1px solid ${ThemePalette.grayscale[0]};
  border-radius: 6px;
  background: ${ThemePalette.surface};
  color: ${ThemePalette.grayscale[4]};
  font: inherit;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: var(--t9-bg-hover);
    color: ${ThemePalette.black};
  }
  &:disabled {
    cursor: default;
  }
  &:focus-visible {
    outline: 2px solid ${ThemePalette.accent};
    outline-offset: 2px;
  }
  svg {
    ${props =>
      props.busy
        ? css`
            animation: swiftSpin 1s linear infinite;
          `
        : ""}
  }
  @keyframes swiftSpin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export type CrumbType = { label: string; to?: string };

const title = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** "Swift > Section > Item" for a path; `itemTitle` names the item a details page shows. */
export function breadcrumbsFor(pathname: string, itemTitle?: string | null): CrumbType[] {
  const parts = pathname.split("/").filter(Boolean);
  const root: CrumbType = { label: "SwiftIQ", to: "/" };
  const page = pathSection(pathname);
  if (!page) return [root, { label: "Overview" }];

  const section = sectionFor(page);
  const item = itemFor(page);
  const crumbs: CrumbType[] = [root];
  if (!section || !item) {
    crumbs.push({ label: title(page.replace(/-/g, " ")) });
    if (parts[1]) crumbs.push({ label: itemTitle || "Details" });
    return crumbs;
  }
  if (section.items.length > 1) {
    crumbs.push({ label: section.label, to: sectionPath(section) });
  }
  if (page === "wizard") {
    crumbs.push({ label: "Transfers", to: "/transfers" });
    crumbs.push({ label: item.label });
    return crumbs;
  }
  if (page === "dr") {
    // /dr/<tab>[/<id>]: the tab, then the open record.
    const tabs: { [tab: string]: string } = {
      vms: "Protected VMs",
      groups: "Protection groups",
      policies: "Policies",
      sites: "Sites",
    };
    const tab = tabs[parts[1]] ? parts[1] : "vms";
    crumbs.push({ label: item.label, to: "/dr" });
    crumbs.push({ label: tabs[tab], to: parts[2] ? `/dr/${tab}` : undefined });
    if (parts[2]) crumbs.push({ label: itemTitle || "Details" });
    return crumbs;
  }
  const hasDetail = Boolean(parts[1]);
  crumbs.push({ label: item.label, to: hasDetail ? `/${page}` : undefined });
  if (hasDetail) crumbs.push({ label: itemTitle || "Details" });
  return crumbs;
}

const isMac = () =>
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");

type Props = {
  crumbs?: CrumbType[];
  /** Extra controls, shown before the search box. */
  right?: React.ReactNode;
};

const TopBar = observer((props: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchRef = React.useRef<HTMLInputElement>(null);
  const [query, setQuery] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [showAbout, setShowAbout] = React.useState(false);
  const [moreOpen, setMoreOpen] = React.useState(false);
  const moreRef = React.useRef<HTMLSpanElement>(null);

  React.useEffect(() => {
    if (!moreOpen) return undefined;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === "Escape" : !moreRef.current?.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    };
    window.addEventListener("mousedown", close);
    window.addEventListener("keydown", close);
    return () => {
      window.removeEventListener("mousedown", close);
      window.removeEventListener("keydown", close);
    };
  }, [moreOpen]);

  // Notifications: loaded here, every 15 s, for whichever page is on screen.
  React.useEffect(() => {
    let stopped = false;
    let timeout: number | undefined;
    const poll = async (showLoading?: boolean) => {
      try {
        await notificationStore.loadData(showLoading);
      } catch (e) {
        // the next poll tries again
      }
      if (!stopped) timeout = window.setTimeout(() => poll(), 15000);
    };
    poll(true);
    return () => {
      stopped = true;
      window.clearTimeout(timeout);
    };
  }, []);

  // Ctrl+K / Cmd+K focuses the search box, as in the Ops Console.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const crumbs = props.crumbs || breadcrumbsFor(location.pathname, shellStore.itemTitle);
  // More than two levels: the earlier ones fold into "...", which opens them as a small menu.
  const hidden = crumbs.length > 2 ? crumbs.slice(0, crumbs.length - 2) : [];
  const shown = crumbs.length > 2 ? crumbs.slice(-2) : crumbs;

  const onSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setQuery("");
      searchRef.current?.blur();
      return;
    }
    if (e.key !== "Enter") return;
    const q = query.trim();
    if (!q) return;
    navigate(`/transfers?q=${encodeURIComponent(q)}`);
    searchRef.current?.blur();
  };

  const onRefresh = () => {
    if (busy) return;
    setBusy(true);
    requestRefresh();
    notificationStore.loadData().catch(() => {});
    window.setTimeout(() => setBusy(false), 600);
  };

  return (
    <Bar>
      {/* Was the sidebar toggle (<MenuButton> with the Menu icon); the sidebar folds from its foot now. */}
      <SearchBox htmlFor="swift-search" title={isMac() ? "Search (⌘K)" : "Search (Ctrl+K)"}>
        <Search size={14} strokeWidth={2} aria-hidden="true" />
        <SearchInput
          id="swift-search"
          ref={searchRef}
          type="search"
          placeholder="Search transfers, endpoints, VMs"
          autoComplete="off"
          aria-label="Search transfers, endpoints and VMs"
          aria-keyshortcuts="Control+K Meta+K"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={onSearchKey}
        />
        <Kbd aria-hidden="true">{isMac() ? "⌘K" : "Ctrl K"}</Kbd>
      </SearchBox>
      <CrumbNav aria-label="Breadcrumb">
        <CrumbList>
          {hidden.length ? (
            <CrumbItem>
              <MoreWrap ref={moreRef}>
                <MoreButton
                  type="button"
                  aria-label="Show the earlier levels"
                  title={hidden.map(c => c.label).join(" › ")}
                  aria-haspopup="menu"
                  aria-expanded={moreOpen}
                  onClick={() => setMoreOpen(o => !o)}
                >
                  …
                </MoreButton>
                {moreOpen ? (
                  <MoreMenu role="menu">
                    {hidden.map((c, i) =>
                      c.to ? (
                        <Link key={`${c.label}-${i}`} role="menuitem" to={c.to} onClick={() => setMoreOpen(false)}>
                          {c.label}
                        </Link>
                      ) : (
                        <span key={`${c.label}-${i}`}>{c.label}</span>
                      ),
                    )}
                  </MoreMenu>
                ) : null}
              </MoreWrap>
            </CrumbItem>
          ) : null}
          {shown.map((c, i) => {
            const last = i === shown.length - 1;
            return (
              <CrumbItem key={`${c.label}-${i}`} current={last} aria-current={last ? "page" : undefined}>
                {i > 0 || hidden.length ? <ChevronRight size={12} strokeWidth={2} aria-hidden="true" /> : null}
                {c.to && !last ? (
                  <Link to={c.to}>{c.label}</Link>
                ) : (
                  <CrumbText title={c.label}>{c.label}</CrumbText>
                )}
              </CrumbItem>
            );
          })}
        </CrumbList>
      </CrumbNav>
      <Right>
        {props.right}
        {/* The search box moved to the left of the bar. */}
        <NotificationDropdown
          items={notificationStore.notificationItems}
          onClose={() => notificationStore.saveSeen()}
        />
        <Status tabIndex={0} role="status" aria-label="Connected">
          <StatusTip aria-hidden="true">Connected</StatusTip>
        </Status>
        <RefreshButton
          type="button"
          busy={busy}
          disabled={busy}
          onClick={onRefresh}
          aria-label="Refresh"
          title={busy ? "Refreshing…" : "Refresh"}
        >
          <RefreshCw size={15} strokeWidth={2} aria-hidden="true" />
          {/* Was the visible text: <span aria-live="polite">{busy ? "Refreshing…" : "Refresh"}</span> */}
        </RefreshButton>
        <UserDropdown
          user={userStore.loggedUser}
          onItemClick={item => {
            if (item.value === "about") setShowAbout(true);
            else if (item.value === "signout") userStore.logout();
          }}
        />
      </Right>
      {showAbout ? <AboutModal onRequestClose={() => setShowAbout(false)} /> : null}
    </Bar>
  );
});

export default TopBar;
